import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, firstValueFrom } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import * as yaml from 'js-yaml';
import { AuthService } from '../auth/auth.service';
import { ConfigService } from '../config.service';
import { ApiCatalogItem, ApiCatalogListResponse, ApiCatalogConfig, ApiVersion, ApiGroupInfo } from '../../models/api-catalog.models';
import { compareVersions } from '../../utils/version-utils';
import { compareStringsWithEmptyLast } from '../../utils/string-utils';

export interface ApiFileInfo {
  category: string;
  title: string;
  description: string;
  version: string;
  content?: string;
  parsedContent?: any;
  apiId?: string;
  versions?: ApiVersion[];
  isDefaultVersion?: boolean;
}

export interface ApiFolderInfo {
  folderName: string;
  displayName: string;
  apiFiles: ApiFileInfo[];
  groupedApis?: ApiGroupInfo[];
}

@Injectable({
  providedIn: 'root'
})
export class ApiCatalogService {
  private readonly config: ApiCatalogConfig;
  private apiCatalogCache?: Observable<ApiCatalogListResponse>;
  private cacheTimestamp?: number;
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private configService: ConfigService
  ) {
    const appConfig = this.configService.getConfig();
    this.config = appConfig.apiCatalogConfig;

    if (!this.config) {
      throw new Error('apiCatalogConfig is not defined in configuration');
    }
  }

  /**
   * Sort API versions using semantic versioning rules and mark the default version
   * @param versionItems - Array of ApiCatalogItem representing different versions of the same API
   * @returns Sorted array of ApiVersion with isDefault flag set on the latest version
   */
  sortApiVersions(versionItems: ApiCatalogItem[]): ApiVersion[] {
    const versions: ApiVersion[] = versionItems.map(item => ({
      apiId: item.apiId,
      version: item.version,
      description: item.description,
      fileName: item.fileName,
      isDefault: false
    }));
    
    // Sort versions in descending order (highest first)
    const sortedVersions = versions.sort((a, b) => 
      compareVersions(b.version, a.version)
    );
    
    // Set default flag on the latest (highest) version
    if (sortedVersions.length > 0) {
      sortedVersions[0].isDefault = true;
    }
    
    return sortedVersions;
  }

  /**
   * Get API folders from API Catalog service with version grouping
   */
  getApiFolders(): Observable<ApiFolderInfo[]> {
    return this.getApiCatalogList().pipe(
      map(response => {
        // Step 1: Group by category
        const groupedByCategory = new Map<string, ApiCatalogItem[]>();
        
        response.data.forEach(item => {
          if (!groupedByCategory.has(item.category)) {
            groupedByCategory.set(item.category, []);
          }
          groupedByCategory.get(item.category)!.push(item);
        });
        
        // Step 2: Process each category
        const folders: ApiFolderInfo[] = [];
        
        groupedByCategory.forEach((items, category) => {
          // Group by title within category
          const groupedByTitle = new Map<string, ApiCatalogItem[]>();
          
          items.forEach(item => {
            if (!groupedByTitle.has(item.title)) {
              groupedByTitle.set(item.title, []);
            }
            groupedByTitle.get(item.title)!.push(item);
          });
          
          // Step 3: Create ApiFileInfo for each title group (showing only default version)
          const apiFiles: ApiFileInfo[] = [];
          
          groupedByTitle.forEach((versionItems, title) => {
            // Sort and get versions for this API
            const sortedVersions = this.sortApiVersions(versionItems);
            const defaultVersion = sortedVersions.find(v => v.isDefault) || sortedVersions[0];
            
            // Create ApiFileInfo representing the API group with default version
            apiFiles.push({
              category: category,
              title: title,
              description: defaultVersion.description,
              version: defaultVersion.version,
              apiId: defaultVersion.apiId,
              versions: sortedVersions,
              isDefaultVersion: true
            });
          });
          
          // Step 4: Create folder info
          folders.push({
            folderName: category,
            displayName: category,
            apiFiles: apiFiles // Sort APIs alphabetically by title
              .sort((a, b) => compareStringsWithEmptyLast(a.title, b.title))
          });
        });

        // Step 5: Sort folders alphabetically by display name
        return folders.sort((a, b) =>
          compareStringsWithEmptyLast(a.displayName, b.displayName)
        );
      }),
      catchError(error => {
        console.error('Error loading API folders from backend:', error);
        return throwError(() => error);
      })
    );
  }

  getApiFiles(): Observable<ApiFileInfo[]> {
    return this.getApiFolders().pipe(
      map(folders => folders.reduce((allFiles: ApiFileInfo[], folder) => 
        [...allFiles, ...folder.apiFiles], []))
    );
  }

  extractFileInfo(yamlContent: string): ApiFileInfo {
    try {
      const parsedContent = yaml.load(yamlContent) as any;
      
      return {
        title: parsedContent?.info?.title || 'Unknown Title',
        description: parsedContent?.info?.description || 'No description available',
        version: parsedContent?.info?.version || 'Unknown Version',
        category: parsedContent?.info?.['x-category'] || 'Unknown Category',
        content: yamlContent,
        parsedContent
      };
    } catch (error) {
      console.error(`Error parsing YAML`, error);
      
      // Fallback to regex parsing
      const titleMatch = yamlContent.match(/title:\s*(.+)/);
      const descriptionMatch = yamlContent.match(/description:\s*(.+)/);
      const versionMatch = yamlContent.match(/version:\s*(.+)/);
      const categoryMatch = yamlContent.match(/x-category:\s*(.+)/);

      return {
        category: categoryMatch ? categoryMatch[1].trim() : 'Unknown Category',
        title: titleMatch ? titleMatch[1].trim() : 'Unknown Title',
        description: descriptionMatch ? descriptionMatch[1].trim() : 'No description available',
        version: versionMatch ? versionMatch[1].trim() : 'Unknown Version',
        content: yamlContent
      };
    }
  }

  /**
   * Get list of all APIs from the catalog-api backend service
   * GET /catalog-api/v1/catalog-apis
   * Results are cached for 10 minutes
   */
  getApiCatalogList(): Observable<ApiCatalogListResponse> {
    const now = Date.now();
    
    if (!this.apiCatalogCache || !this.cacheTimestamp || (now - this.cacheTimestamp) > this.CACHE_DURATION) {
      this.cacheTimestamp = now;
      this.apiCatalogCache = new Observable<ApiCatalogListResponse>(observer => {
        this.createHeaders().then(headers => {
          const url = `${this.config.baseUrl}/${this.config.version}/catalog-apis`;
          
          this.http.get<ApiCatalogListResponse>(url, { headers })
            .pipe(
              catchError(error => {
                console.error('Error fetching API catalog list:', error);
                return throwError(() => new Error('Failed to fetch API catalog list'));
              })
            )
            .subscribe(observer);
        }).catch(error => {
          observer.error(new Error('Failed to create request headers'));
        });
      }).pipe(
        shareReplay(1) // Share the result with all subscribers
      );
    }
    
    return this.apiCatalogCache;
  }

  /**
   * Get API specification by ID from the catalog-api backend service
   * GET /catalog-api/v1/catalog-apis/{apiId}/yaml-spec
   * @param apiId - Unique API identifier (GUID)
   * @returns Observable with YAML content as string
   */
  getApiSpecById(apiId: string): Observable<string> {
    return new Observable(observer => {
      this.createHeaders().then(headers => {
        const url = `${this.config.baseUrl}/${this.config.version}/catalog-apis/${apiId}/yaml-spec`;
        // Remove Content-Type for YAML response
        headers = headers.delete('Content-Type');
        
        this.http.get(url, { headers, responseType: 'text' })
          .pipe(
            catchError(error => {
              console.error(`Error fetching API spec for ${apiId}:`, error);
              return throwError(() => new Error(`Failed to fetch API specification for ${apiId}`));
            })
          )
          .subscribe(observer);
      }).catch(error => {
        observer.error(new Error('Failed to create request headers'));
      });
    });
  }
  
  /**
   * Create HTTP headers with Authorization and subscription key for API Catalog requests
   * @private
   */
  private async createHeaders(): Promise<HttpHeaders> {
    let headers = new HttpHeaders({
      'X-Subscription-Key': this.config.subscriptionKey,
      'Content-Type': 'application/json'
    });

    try {
      const accessToken = await firstValueFrom(this.authService.getAccessToken());
      if (accessToken) {
        headers = headers.set('Authorization', `Bearer ${accessToken}`);
      }
    } catch (error) {
      console.warn('Failed to get access token for API Catalog request:', error);
    }

    return headers;
  }
}

